import React, { useRef, useState } from 'react';
import { BookOpen, Eye, LockKeyhole, X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle, DialogDescription, DialogClose } from '../ui/dialog';

const sourceLabel = entry => ({ website: 'Website', google_drive: 'Google Drive', manual: 'Document' }[entry.source_type] || 'Document');

export const KnowledgePicker = ({ entries, selectedIds, onToggle, isDarkMode }) => {
  const [preview, setPreview] = useState(null);
  const opener = useRef(null);
  return <>
    <div className="agent-kb-list" data-testid="agent-kb-list">
      {entries.map(entry => {
        const selected = selectedIds.includes(entry.kb_id);
        return <div key={entry.kb_id} className="agent-kb-row" data-selected={selected} data-testid={`kb-row-${entry.kb_id}`}>
          <label className="agent-kb-choice" data-testid={`kb-option-${entry.kb_id}`}>
            <input type="checkbox" checked={selected}
              onChange={event => onToggle(entry, event)} data-testid={`kb-toggle-${entry.kb_id}`} />
            <span className="agent-kb-copy">
              <strong data-testid={`kb-name-${entry.kb_id}`}>{entry.name || 'Untitled knowledge base'}</strong>
              <span className="agent-kb-description" data-testid={`kb-description-${entry.kb_id}`}>
                {entry.description || (typeof entry.content === 'string' ? entry.content.replace(/\s+/g, ' ').slice(0, 180) : '') || 'No description available.'}
              </span>
              <small data-testid={`kb-status-${entry.kb_id}`}>
                {sourceLabel(entry)}{entry.enabled === false ? ' · Disabled' : selected ? ' · Selected' : ''}
              </small>
            </span>
          </label>
          <button type="button" className="agent-kb-view" data-testid={`kb-view-${entry.kb_id}`}
            aria-label={`View ${entry.name || 'knowledge base'}`} onClick={event => {
              opener.current = event.currentTarget;
              setPreview(entry);
            }}><Eye size={15} aria-hidden="true" /><span>View</span></button>
        </div>;
      })}
    </div>
    <Dialog open={Boolean(preview)} onOpenChange={open => { if (!open) setPreview(null); }}>
      <DialogPortal>
        <DialogOverlay className="agent-kb-overlay" data-testid="kb-preview-overlay" />
        <DialogPrimitive.Content className="agent-config agent-kb-preview" data-theme={isDarkMode ? 'dark' : 'light'}
          data-testid="kb-preview-dialog" onCloseAutoFocus={event => { event.preventDefault(); opener.current?.focus(); }}>
          <header className="agent-kb-preview-header">
            <span className="agent-kb-preview-icon"><BookOpen size={20} aria-hidden="true" /></span>
            <div className="min-w-0 flex-1">
              <span className="agent-kb-readonly" data-testid="kb-preview-readonly"><LockKeyhole size={11} />Read only</span>
              <DialogTitle className="agent-kb-preview-title" data-testid="kb-preview-title">{preview?.name || 'Knowledge base'}</DialogTitle>
              <DialogDescription className="agent-kb-preview-description" data-testid="kb-preview-description">
                {preview?.description || `${preview ? sourceLabel(preview) : 'Document'} · Knowledge base preview`}
              </DialogDescription>
            </div>
            <DialogClose className="agent-config-icon-button" aria-label="Close knowledge base preview" data-testid="kb-preview-close">
              <X size={18} />
            </DialogClose>
          </header>
          <div className="agent-kb-preview-scroll" tabIndex={0} role="region" aria-label="Knowledge base content" data-testid="kb-preview-scroll">
            <pre className="agent-kb-preview-content" data-testid="kb-preview-content">
              {typeof preview?.content === 'string' && preview.content.trim() ? preview.content : 'No text content is available for this knowledge base.'}
            </pre>
          </div>
          <footer className="agent-kb-preview-footer">
            <span data-testid="kb-preview-selection-status">{preview && selectedIds.includes(preview.kb_id) ? 'Selected for this agent' : 'Not selected for this agent'}</span>
            <DialogClose className="agent-config-secondary-button" data-testid="kb-preview-done">Close preview</DialogClose>
          </footer>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  </>;
};